/**
 * Custom hooks for dashboard data
 */

import { useQuery } from '@tanstack/react-query';
import { dashboardService } from '../services/dashboardService';

/**
 * Hook to fetch dashboard summary statistics
 * @returns {Object} Query result with stats data
 */
export function useDashboardStats() {
  return useQuery({
    queryKey: ['dashboard', 'stats'],
    queryFn: dashboardService.getDashboardStats,
    staleTime: 1000 * 60
  });
}

/**
 * Hook to fetch bottles by type statistics
 * @returns {Object} Query result with type data
 */
export function useBottlesByType() {
  return useQuery({
    queryKey: ['dashboard', 'bottlesByType'],
    queryFn: dashboardService.getBottlesByType,
    staleTime: 1000 * 60
  });
}

/**
 * Hook to fetch bottles by status statistics
 * @returns {Object} Query result with status data
 */
export function useBottlesByStatus() {
  return useQuery({
    queryKey: ['dashboard', 'bottlesByStatus'],
    queryFn: dashboardService.getBottlesByStatus,
    staleTime: 1000 * 60
  });
}

/**
 * Hook to fetch bottles by state/region statistics
 * @returns {Object} Query result with state data
 */
export function useBottlesByState() {
  return useQuery({
    queryKey: ['dashboard', 'bottlesByState'],
    queryFn: dashboardService.getBottlesByState,
    staleTime: 1000 * 60
  });
}

/**
 * Hook to fetch bottles by purchase price range statistics
 * @returns {Object} Query result with price range data
 */
export function useBottlesByPriceRange() {
  return useQuery({
    queryKey: ['dashboard', 'bottlesByPriceRange'],
    queryFn: dashboardService.getBottlesByPriceRange,
    staleTime: 1000 * 60
  });
}

/**
 * Hook to fetch bottles by country statistics
 * @returns {Object} Query result with country data
 */
export function useBottlesByCountry() {
  return useQuery({
    queryKey: ['dashboard', 'bottlesByCountry'],
    queryFn: dashboardService.getBottlesByCountry,
    staleTime: 1000 * 60
  });
}

/**
 * Hook to fetch distilleries by country statistics
 * @returns {Object} Query result with country data
 */
export function useDistilleriesByCountry() {
  return useQuery({
    queryKey: ['dashboard', 'distilleriesByCountry'],
    queryFn: dashboardService.getDistilleriesByCountry,
    staleTime: 1000 * 60
  });
}

/**
 * Hook to fetch recent bottles
 * @param {number} limit - Number of bottles to fetch
 * @returns {Object} Query result with recent bottles
 */
export function useRecentBottles(limit = 5) {
  return useQuery({
    queryKey: ['dashboard', 'recentBottles', limit],
    queryFn: () => dashboardService.getRecentBottles(limit),
    staleTime: 1000 * 60
  });
}

/**
 * Hook to fetch recently emptied bottles
 * @param {number} limit - Number of bottles to fetch
 * @returns {Object} Query result with recently emptied bottles
 */
export function useRecentlyEmptiedBottles(limit = 5) {
  return useQuery({
    queryKey: ['dashboard', 'recentlyEmptiedBottles', limit],
    queryFn: () => dashboardService.getRecentlyEmptiedBottles(limit),
    staleTime: 1000 * 60
  });
}

/**
 * Hook to fetch ordered bottles
 * @param {number} limit - Number of bottles to fetch
 * @returns {Object} Query result with ordered bottles
 */
export function useOrderedBottles() {
  return useQuery({
    queryKey: ['dashboard', 'orderedBottles'],
    queryFn: dashboardService.getOrderedBottles,
    staleTime: 1000 * 60
  });
}
